import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Maximize2, MoreVertical, X, FileText, Trash2 } from 'lucide-react';

/**
 * Standalone PDF Slides Renderer for static HTML rendering contexts.
 * Uses native browser fullscreen API (requestFullscreen/exitFullscreen).
 */

const PdfSlidesStandaloneViewer = ({ images, filename, readOnly = false }) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const containerRef = useRef(null);
  
  const totalPages = images.length;
  const API_URL = process.env.REACT_APP_BACKEND_URL || '';
  
  const goToPage = useCallback((page) => {
    if (page >= 0 && page < totalPages) {
      setCurrentPage(page);
    }
  }, [totalPages]);
  
  const toggleFullscreen = useCallback(async () => {
    try {
      if (!document.fullscreenElement) {
        if (containerRef.current) {
          await containerRef.current.requestFullscreen();
        }
      } else {
        await document.exitFullscreen();
      }
    } catch (err) {
      console.error('Fullscreen error:', err);
    }
  }, []);
  
  // Listen for native fullscreen change
  useEffect(() => {
    const handleFsChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFsChange);
    return () => document.removeEventListener('fullscreenchange', handleFsChange);
  }, []);
  
  // Keyboard navigation in fullscreen
  useEffect(() => {
    if (!isFullscreen) return;
    const handleKey = (e) => {
      if (e.key === 'ArrowLeft') { e.preventDefault(); goToPage(currentPage - 1); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); goToPage(currentPage + 1); }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isFullscreen, currentPage, goToPage]);
  
  if (totalPages === 0) return null;
  
  return (
    <div 
      ref={containerRef}
      className={`relative ${isFullscreen ? 'bg-black flex flex-col h-full w-full' : 'my-4'}`}
    >
      {/* Slide image */}
      <div className={`relative ${isFullscreen ? 'flex-1 flex items-center justify-center overflow-hidden' : 'rounded-t-lg overflow-hidden border border-b-0'}`}>
        {!isFullscreen && (
          <div className="absolute top-3 right-3 z-10 bg-black/60 backdrop-blur-sm text-white text-xs font-medium px-3 py-1.5 rounded-md">
            PDF Slides
          </div>
        )}
        <img
          src={`${API_URL}${images[currentPage]}`}
          alt={`${filename} - Page ${currentPage + 1}`}
          className={isFullscreen ? 'max-h-full max-w-full object-contain' : 'w-full block'}
          style={!isFullscreen ? { minHeight: 300, background: '#0d1117' } : {}}
          draggable={false}
        />
      </div>
      
      {/* Bottom bar */}
      <div className={`flex items-center justify-between px-3 py-2 ${
        isFullscreen 
          ? 'bg-gray-900/90 backdrop-blur-sm border-t border-gray-700' 
          : 'bg-card border border-t-0 rounded-b-lg'
      }`}>
        {/* Left: nav */}
        <div className="flex items-center gap-1">
          <button
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage === 0}
            className={`p-1.5 rounded-md transition-colors ${
              currentPage === 0 
                ? 'opacity-30 cursor-not-allowed' 
                : isFullscreen ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-muted text-muted-foreground'
            }`}
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          
          <span className={`text-sm font-medium min-w-[2ch] text-center tabular-nums ${isFullscreen ? 'text-gray-200' : ''}`}>
            {currentPage + 1}
          </span>
          
          <button
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage === totalPages - 1}
            className={`p-1.5 rounded-md transition-colors ${
              currentPage === totalPages - 1 
                ? 'opacity-30 cursor-not-allowed' 
                : isFullscreen ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-muted text-muted-foreground'
            }`}
          >
            <ChevronRight className="h-4 w-4" />
          </button>
          
          {/* Menu */}
          <div className="relative ml-1">
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className={`p-1.5 rounded-md transition-colors ${
                isFullscreen ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-muted text-muted-foreground'
              }`}
            >
              <MoreVertical className="h-4 w-4" />
            </button>
            
            {menuOpen && (
              <>
                <div className="fixed inset-0 z-50" onClick={() => setMenuOpen(false)} />
                <div className={`absolute bottom-full left-0 mb-2 z-50 min-w-[220px] rounded-lg shadow-xl border py-1.5 ${
                  isFullscreen ? 'bg-gray-800 border-gray-600' : 'bg-popover border-border'
                }`}>
                  <button
                    onClick={() => { toggleFullscreen(); setMenuOpen(false); }}
                    className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2.5 transition-colors ${
                      isFullscreen ? 'hover:bg-gray-700 text-gray-200' : 'hover:bg-muted'
                    }`}
                  >
                    {isFullscreen ? <X className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                    {isFullscreen ? 'Quitter le plein écran' : 'Activer le mode plein écran'}
                  </button>
                  <button
                    onClick={() => { goToPage(0); setMenuOpen(false); }}
                    className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2.5 transition-colors ${
                      isFullscreen ? 'hover:bg-gray-700 text-gray-200' : 'hover:bg-muted'
                    }`}
                  >
                    <FileText className="h-4 w-4" />
                    Revenir à la première page
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
        
        {/* Right */}
        <div className="flex items-center gap-3">
          <span className={`text-sm tabular-nums ${isFullscreen ? 'text-gray-400' : 'text-muted-foreground'}`}>
            {currentPage + 1} / {totalPages}
          </span>
          
          <button
            onClick={toggleFullscreen}
            className={`p-1.5 rounded-md transition-colors ${
              isFullscreen ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-muted text-muted-foreground'
            }`}
            title={isFullscreen ? 'Quitter le plein écran' : 'Plein écran'}
          >
            {isFullscreen ? <X className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </button>
          
          <span className={`text-xs font-medium flex items-center gap-1.5 ${
            isFullscreen ? 'text-gray-400' : 'text-muted-foreground'
          }`}>
            <FileText className="h-3.5 w-3.5" />
            PDF Slides
          </span>
        </div>
      </div>
    </div>
  );
};

/**
 * Component wrapper: renders HTML content and replaces PDF slides divs with interactive viewers.
 * Drop-in replacement for dangerouslySetInnerHTML when PDF slides support is needed.
 */
export const HtmlContentWithPdfSlides = ({ html, className = '' }) => {
  const containerRef = useRef(null);
  const [pdfSlidesData, setPdfSlidesData] = useState([]);

  // Process HTML to ensure all iframes have allowfullscreen (e.g. for Google Slides, YouTube)
  const processedHtml = useMemo(() => {
    if (!html) return '';
    if (!html.includes('<iframe')) return html;
    
    try {
      const parser = new DOMParser();
      
      const processIframes = (documentNode) => {
        let isModified = false;
        const iframes = documentNode.querySelectorAll('iframe');
        iframes.forEach(iframe => {
          if (!iframe.hasAttribute('allowfullscreen')) {
            iframe.setAttribute('allowfullscreen', 'true');
            iframe.setAttribute('webkitallowfullscreen', 'true');
            iframe.setAttribute('mozallowfullscreen', 'true');
            isModified = true;
          }
          let allowAttr = iframe.getAttribute('allow') || '';
          if (!allowAttr.includes('fullscreen')) {
            iframe.setAttribute('allow', allowAttr ? `${allowAttr}; fullscreen` : 'fullscreen');
            isModified = true;
          }

          let srcdoc = iframe.getAttribute('srcdoc');
          if (srcdoc) {
            let innerDoc = parser.parseFromString(srcdoc, 'text/html');
            if (processIframes(innerDoc)) {
              iframe.setAttribute('srcdoc', innerDoc.body.innerHTML);
              isModified = true;
            }
          }
        });
        return isModified;
      };

      const doc = parser.parseFromString(html, 'text/html');
      return processIframes(doc) ? doc.body.innerHTML : html;
    } catch (e) {
      console.error('Error processing HTML iframes:', e);
      return html;
    }
  }, [html]);
  
  useEffect(() => {
    if (!containerRef.current || !processedHtml) return;
    
    // After rendering, find PDF slides containers
    const timer = setTimeout(() => {
      const pdfElements = containerRef.current?.querySelectorAll('[data-pdf-slides]');
      if (!pdfElements) return;
      
      const data = [];
      pdfElements.forEach((el, index) => {
        try {
          const images = JSON.parse(el.getAttribute('data-images') || '[]');
          const filename = el.getAttribute('data-filename') || 'PDF';
          if (images.length > 0) {
            el.style.display = 'none';
            data.push({ images, filename, id: `pdf-${index}` });
          }
        } catch (err) {
          console.error('Error parsing PDF slides:', err);
        }
      });
      setPdfSlidesData(data);
    }, 100);
    
    return () => clearTimeout(timer);
  }, [processedHtml]);
  
  // Build content: split HTML at PDF slides boundaries and interleave viewers
  const parts = [];
  if (processedHtml && pdfSlidesData.length > 0) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(processedHtml, 'text/html');
    const body = doc.body;
    
    let currentHtml = '';
    let slideIndex = 0;
    
    for (const child of body.childNodes) {
      if (child.nodeType === 1 && child.hasAttribute && child.hasAttribute('data-pdf-slides')) {
        if (currentHtml.trim()) {
          parts.push({ type: 'html', content: currentHtml, key: `html-${parts.length}` });
          currentHtml = '';
        }
        if (slideIndex < pdfSlidesData.length) {
          parts.push({ type: 'slides', data: pdfSlidesData[slideIndex], key: `slides-${slideIndex}` });
          slideIndex++;
        }
      } else {
        if (child.outerHTML) {
          currentHtml += child.outerHTML;
        } else if (child.textContent) {
          currentHtml += child.textContent;
        }
      }
    }
    if (currentHtml.trim()) {
      parts.push({ type: 'html', content: currentHtml, key: `html-${parts.length}` });
    }
  }
  
  if (parts.length > 0) {
    return (
      <div ref={containerRef} className={className}>
        {parts.map((part) => {
          if (part.type === 'html') {
            return <div key={part.key} dangerouslySetInnerHTML={{ __html: part.content }} />;
          } else if (part.type === 'slides') {
            return (
              <PdfSlidesStandaloneViewer
                key={part.key}
                images={part.data.images}
                filename={part.data.filename}
                readOnly={true}
              />
            );
          }
          return null;
        })}
      </div>
    );
  }
  
  // Fallback: simple render with post-processing
  return (
    <div ref={containerRef} className={className}>
      <div dangerouslySetInnerHTML={{ __html: processedHtml || '' }} />
      {pdfSlidesData.map((slide) => (
        <PdfSlidesStandaloneViewer
          key={slide.id}
          images={slide.images}
          filename={slide.filename}
          readOnly={true}
        />
      ))}
    </div>
  );
};

export default PdfSlidesStandaloneViewer;
